import { RefCallback, useId } from 'react';
import { LuArrowRight, LuClock, LuPiggyBank, LuRocket, LuX } from 'react-icons/lu';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { cn } from 'utils/cn';

const boostTitle = 'Create an Investment Policy with Portfolio Boost';

interface EnrollButtonProps {
    onEnroll: () => void;
    disabledReason: string | null;
    compact?: boolean;
}

const EnrollButton = ({ onEnroll, disabledReason, compact = false }: EnrollButtonProps) => {
    const reasonId = useId();
    const button = (
        <Button
            disabled={!!disabledReason}
            aria-describedby={disabledReason ? reasonId : undefined}
            onClick={onEnroll}
            className={cn('rounded-lg bg-white text-[#1f1a19] hover:bg-white/90', compact ? 'h-9 px-4' : 'h-12 px-6')}
        >
            {!compact && <LuRocket aria-hidden="true" className="mr-2 h-5 w-5" />} Enroll in Boost
        </Button>
    );
    if (!disabledReason) return button;
    return (
        <div className="flex flex-col items-start gap-2">
            {button}
            <p id={reasonId} className="max-w-64 text-sm text-white/70">
                {disabledReason}
            </p>
        </div>
    );
};

interface BoostBannerProps {
    dismissed: boolean;
    sectionRef: RefCallback<HTMLElement>;
    enrollmentPath: string;
    onEnroll: () => void;
    disabledReason: string | null;
    onDismiss: () => void;
    onRestore: () => void;
}

export const BoostBanner = ({
    dismissed,
    sectionRef,
    enrollmentPath,
    onEnroll,
    disabledReason,
    onDismiss,
    onRestore,
}: BoostBannerProps) => (
    <section
        aria-labelledby="policy-boost-title"
        ref={sectionRef}
        className={cn(
            'flex scroll-mt-4 flex-wrap bg-[#2c2423] text-white',
            dismissed
                ? 'mb-10 items-center gap-x-4 gap-y-3 rounded-xl p-3.5 pl-4.5'
                : 'mb-12 overflow-hidden rounded-2xl',
        )}
    >
        {dismissed ? (
            <>
                <LuRocket aria-hidden="true" className="h-5 w-5 shrink-0 text-[#7CC0C8]" />
                <h2 id="policy-boost-title" className="min-w-0 flex-[1_1_280px] text-sm font-medium">
                    {boostTitle}
                </h2>
                <div className="flex flex-wrap gap-2">
                    <Button
                        variant="outline"
                        className="h-9 rounded-lg border-white/25 bg-transparent px-3.5 text-white hover:bg-white/10 hover:text-white"
                        onClick={onRestore}
                    >
                        Learn More
                    </Button>
                    <EnrollButton onEnroll={onEnroll} disabledReason={disabledReason} compact />
                </div>
            </>
        ) : (
            <>
                <div className="min-w-0 flex-[1.75_1_380px] p-6 lg:p-8">
                    <div className="inline-flex items-center gap-2 rounded-md bg-white/10 px-3 py-2 text-xs font-medium tracking-widest text-[#7CC0C8] uppercase">
                        <LuRocket aria-hidden="true" className="h-4 w-4" /> Portfolio Boost
                    </div>
                    <h2 id="policy-boost-title" className="mt-5 mb-4 text-2xl font-medium tracking-tight">
                        {boostTitle}
                    </h2>
                    <p className="max-w-[640px] text-base leading-relaxed text-[#DCD4CF]">
                        Managed by hand today. On the engine it stays on target every trading day — rebalancing, cash,
                        billing, and tax-aware trading — and you keep every override. 0.15% annually on boosted assets,
                        billed quarterly.
                    </p>
                    <div className="mt-8 flex flex-wrap items-center gap-3">
                        <EnrollButton onEnroll={onEnroll} disabledReason={disabledReason} />
                        <Dialog>
                            <DialogTrigger asChild>
                                <Button
                                    variant="outline"
                                    className="h-12 rounded-lg border-white/25 bg-transparent px-6 text-white hover:bg-white/10 hover:text-white"
                                >
                                    Learn More
                                </Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader>
                                    <DialogTitle>Portfolio Boost</DialogTitle>
                                    <DialogDescription>
                                        Automatic account management keeps investments aligned with a target allocation.
                                    </DialogDescription>
                                </DialogHeader>
                                <p className="text-cocoa-600 text-sm leading-relaxed">
                                    The portfolio engine manages rebalancing and cash needs, with tax-loss harvesting
                                    when enabled. Review the portfolio’s management and target allocation setup before
                                    making changes.
                                </p>
                                <Button asChild>
                                    <Link to={enrollmentPath}>Open portfolio overview</Link>
                                </Button>
                            </DialogContent>
                        </Dialog>
                        <Button
                            variant="ghost"
                            className="h-12 text-white/70 hover:bg-white/10 hover:text-white"
                            onClick={onDismiss}
                        >
                            <LuX aria-hidden="true" className="mr-2 h-4 w-4" /> Dismiss
                        </Button>
                    </div>
                </div>
                <div className="flex min-w-0 flex-[1_1_220px] flex-col gap-5 border-t border-white/15 p-6 lg:border-t-0 lg:border-l lg:p-8">
                    <h3 className="text-xs font-medium tracking-widest text-[#CFC6C1] uppercase">
                        What Boost returns in year one
                    </h3>
                    <div className="flex flex-wrap gap-7">
                        <div className="min-w-0 flex-[1_1_150px]">
                            <div className="flex items-center gap-3 text-2xl font-medium">
                                <LuPiggyBank aria-hidden="true" className="h-6 w-6 text-[#CFC6C1]" />
                                <span aria-label="Savings estimate unavailable">—</span>
                            </div>
                            <p className="mt-1 text-sm leading-snug text-[#DCD4CF]">
                                Estimated savings and optimizations for this account
                            </p>
                        </div>
                        <div className="min-w-0 flex-[1_1_150px]">
                            <div className="flex items-center gap-3 text-2xl font-medium">
                                <LuClock aria-hidden="true" className="h-6 w-6 text-[#CFC6C1]" />
                                <span aria-label="Time saved estimate unavailable">—</span>
                            </div>
                            <p className="mt-1 text-sm leading-snug text-[#DCD4CF]">Advisor time saved monthly</p>
                        </div>
                    </div>
                    <Dialog>
                        <DialogTrigger asChild>
                            <button
                                type="button"
                                className="flex cursor-pointer items-center gap-2 self-start text-sm font-medium hover:underline focus-visible:outline-2 focus-visible:outline-white"
                            >
                                How this is calculated <LuArrowRight aria-hidden="true" className="h-4 w-4" />
                            </button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Portfolio Boost estimates</DialogTitle>
                                <DialogDescription>
                                    Account-specific savings and advisor time-saved estimates are not currently
                                    available. No estimate is shown until a calculation is available for this account.
                                </DialogDescription>
                            </DialogHeader>
                        </DialogContent>
                    </Dialog>
                </div>
            </>
        )}
    </section>
);
